import { useEffect, useRef } from "react"
import { Hint } from "@kit/design/hint"

/**
 * Shows a {@link Hint} coachmark while `open` is true, anchored to
 * `anchorRef.current`, and closes it when `open` becomes false or the component
 * unmounts. Dismissing the hint (its close button) calls `onDismiss`.
 *
 * Only `open` drives showing/closing — the content and anchor are read from a
 * ref so a new inline `onDismiss`/options object each render doesn't re-show the
 * hint.
 */
export function useOptInHint({
  open,
  anchorRef,
  onDismiss,
  title,
  description,
  position,
  align
}) {
  const latest = useRef()
  latest.current = { anchorRef, onDismiss, title, description, position, align }

  useEffect(() => {
    if (!open) return undefined

    const { anchorRef, onDismiss, title, description, position, align } =
      latest.current
    const anchor = anchorRef.current
    if (!anchor) return undefined

    const id = Hint.show({
      anchor,
      title,
      description,
      position,
      align,
      onClose: onDismiss
    })
    return () => Hint.close(id)
  }, [open])
}
